import React, { memo, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import InputDatePickerRange from 'src/components/common/field/InputDatePickerRange';
import DropDown from 'src/components/common/field/DropDown';
import { getToken } from 'src/utils/auth';
import { postAPI } from 'src/utils/api';
import { getField, getToday, isEmpty } from 'src/common/helper';
import { useNotify } from 'src/context/ContextNotify';

import dayjs from 'dayjs';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '90px'
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
  '&.MuiInputLabel-root': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

const formDataDefine = {
  startDate: getToday(),
  endDate: getToday(),
  doctorid: null
};

const defaultError = {
  startDate: true,
  doctorid: true
};

type FormDataType = {
  startDate: string | null;
  endDate: string | null;
  doctorid: number | string | null;
};

interface DialogFormScheduleProps {
  open: boolean;
  data: any;
  doctors: any[];
  yearMonth: string;
  onClose: (isRefreshList?: boolean) => void;
};

const DialogFormSchedule: React.FC<DialogFormScheduleProps> = (props) => {
  const notifyContext = useNotify();
  const [formData, setFormData] = useState<FormDataType>(formDataDefine);
  const [errors, setErrors] = useState(defaultError);

  const getDoctorName = () => {
    const find = props.doctors.find((item: any) => Number(item.id) === Number(getField(props.data, 'doctorid', 0)));

    return find ? getField(find, 'name', '-') : '-';
  };

  const getDays = () => {
    let start = dayjs(formData.startDate);
    let end = dayjs(formData.endDate);
    const list: any = [];

    while (start.format('YYYY-MM-DD') <= end.format('YYYY-MM-DD')) {
      list.push(start.format('YYYY-MM-DD'));
      start = start.add(1, 'day');
    }

    return list;
  };

  const validate = () => {
    if (isEmpty(formData.startDate)) {
      setErrors({ ...defaultError, startDate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '日付を選択して下さい',
        type: 'error'
      });
      return false;
    }

    if (isEmpty(formData.doctorid)) {
      setErrors({ ...defaultError, doctorid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '医師を選択してください',
        type: 'error'
      });
      return false;
    }
    setErrors(defaultError);
    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });
    return true;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        month: props.yearMonth,
        doctorid: formData.doctorid,
        days: getDays()
      };

      postAPI(`/calendar/set-schedule?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            props.onClose(true);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleChangeDoctor = (id: number | string | null) => {
    setFormData((prevState) => ({
      ...prevState,
      doctorid: id
    }))
  };

  const handleChangeDateRange = (
    startDate: string | null,
    endDate: string | null
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      startDate,
      endDate
    }))
  };

  const handleClose = () => {
    props.onClose();
  };

  const initData = () => {
    setFormData(props.data);
  };

  useEffect(() => {
    if (props.open) {
      initData();
    } else {
      setErrors(defaultError);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '378px',
          height: '248px',
          m: '0px',
          overflow: 'visible'
        }
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            勤務日編集
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 24px',
          overflow: 'visible'
        }}
      >
        <Stack spacing={getField(props.data, 'doctorid', null) !== null ? '20px': '10px'} mt={'10px'}>
          {getField(props.data, 'doctorid', null) !== null && (
            <Stack direction={'row'} alignItems={'center'}>
              <Typography sx={{ ...typographyLabel }}>
                医師
              </Typography>
              <Typography sx={{ ...typographyLabel }}>
                {getDoctorName()}
              </Typography>
            </Stack>
          )}

          <Stack direction={'row'} alignItems={'center'}>
            <Typography sx={{ ...typographyLabel }}>
              日付
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            </Typography>
            <InputDatePickerRange
              startDate={formData.startDate}
              endDate={formData.endDate}
              placeholder='期間'
              sxInput={{ height: '48px', width: '240px', fontSize: '12px' }}
              onChange={handleChangeDateRange}
              error={!errors.startDate}
            />
          </Stack>

          {getField(props.data, 'doctorid', null) === null && (
            <Stack direction={'row'} alignItems={'center'}>
              <Typography sx={{ ...typographyLabel }}>
                医師
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <DropDown
                selected={formData.doctorid}
                idSelect='doctor-select'
                placeholder='選択してください'
                items={props.doctors}
                onChange={handleChangeDoctor}
                sxInput={{ width: '240px' }}
                sxLabel={sxLableDropDown}
                error={!errors.doctorid}
              />
            </Stack>
          )}

        </Stack>
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px 24px '
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} />
        <BtnGradient text='登録' handleButtonClick={handleSave} />
      </DialogActions>
    </Dialog>
  );
};

export default memo(DialogFormSchedule);
